import { inject, injectable, injectFromBase } from 'inversify'
import { action } from 'mobx'
import { FeedbackPresenter } from '@core/feedback/feedback.presenter'
import type { FeedbackScope } from '@core/feedback/feedback.config'
import { ValidationRunner } from './validation.runner'
import type { Schema } from './validation.schema'
import type { ValidationResult } from './validation.types'

/**
 * Client-side validation for presenters that own a form.
 *
 * Extends FeedbackPresenter rather than composing a validator, because a
 * validation failure *is* feedback — it needs a scope, a tone and a surface,
 * all of which the base already provides. Accepted cost, the same one draft §J
 * records for LoadableRepository: TypeScript has single inheritance and this
 * spends the slot. A third orthogonal concern wanting the same seat is the
 * signal to switch to an injected collaborator, not to deepen the chain.
 *
 * Replaces the old ValidationProvider React context, which held the app's only
 * state outside a Repository, was invisible to every test, and was never
 * actually mounted (defect 20).
 */
@injectable()
@injectFromBase()
export abstract class ValidatingPresenter extends FeedbackPresenter {
  @inject(ValidationRunner)
  protected validationRunner!: ValidationRunner

  /**
   * Validate `value` against `schema`. On failure the issues are placed on this
   * presenter's own feedback scope as a `warning` — not an `error`, because
   * nothing was attempted: the user has not asked the server anything yet, and
   * `isError` must stay reserved for something that actually failed (§V.1).
   *
   * Returns the result so a caller can branch on it, rather than a bare boolean
   * — a valid result carries the parsed value, which is the point of a schema.
   */
  protected validate = <T>(schema: Schema<T>, value: unknown): ValidationResult<T> => {
    const result = this.validationRunner.validate(schema, value)

    if (result.valid) {
      this.clearValidation()
    } else {
      this.messagesRepository.set(
        this.feedbackScope,
        result.issues.map((issue) => issue.message),
        'warning',
      )
    }

    return result
  }

  /**
   * Manual, non-derived messages on this presenter's own surface — the reason
   * `show()` exists (§V.1), wrapped so a form presenter does not have to name
   * its own scope every time.
   */
  warn = (message: string, scope: FeedbackScope = this.feedbackScope): void => {
    this.show(scope, message, 'warning')
  }

  inform = (message: string, scope: FeedbackScope = this.feedbackScope): void => {
    this.show(scope, message, 'info')
  }

  /**
   * Only clears a channel this presenter put *validation* in. A server error
   * sitting on the same scope is not this method's to remove — it describes a
   * different, later event.
   */
  protected clearValidation = action((): void => {
    if (this.messagesRepository.channelFor(this.feedbackScope).tone === 'warning') {
      this.messagesRepository.reset(this.feedbackScope)
    }
  })
}
